import { all, put, takeLatest } from "redux-saga/effects";
import queryApi from "../../utils/fetchApi";
import Urler from "../../utils/serviceUrl";
import {
  checkRoundCompletionFailure,
  checkRoundCompletionSuccess,
  fetchAllPairingsFailure,
  fetchAllPairingsSuccess,
  fetchChallengePairingsFailure,
  fetchChallengePairingsSuccess,
  fetchCreatorPairingsFailure,
  fetchCreatorPairingsSuccess,
  fetchMyPairStatusFailure,
  fetchMyPairStatusSuccess,
  fetchUserPairingsFailure,
  fetchUserPairingsSuccess,
  submitLosersFailure,
  submitLosersSuccess,
  submitWinnersFailure,
  submitWinnersSuccess,
  updateLosersFailure,
  updateLosersSuccess,
  updateWinnersFailure,
  updateWinnersSuccess,
  verifyLosersFailure,
  verifyLosersSuccess,
  verifyWinnersFailure,
  verifyWinnersSuccess,
  fetchPairsWinnersSuccess,
  fetchPairsWinnersFailure,
} from "./pairings.actions";
import pairingsActionTypes from "./pairings.types";

const isSuccessResponse = (response) =>
  response &&
  (response.status === "Success" ||
    response.status === "success" ||
    response.status === "sucess");

const normalizePairingsResponse = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response.data?.myPairs) return response.data.myPairs;
  if (response.data?.pairs) return response.data.pairs;
  if (response.data?.data) return response.data.data;
  if (response.myPairs) return response.myPairs;
  return response.data || [];
};

function* fetchAllPairings({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("/pairs"),
      bearer: token,
    });
    if (isSuccessResponse(res)) {
      yield put(fetchAllPairingsSuccess(normalizePairingsResponse(res)));
    } else if (res.status === 403 || res.statusCode === 403) {
      // User lacks permission to view all pairings, treat as empty success
      yield put(fetchAllPairingsSuccess([]));
    } else {
      yield put(
        fetchAllPairingsFailure(res.message || "Unable to load pairings"),
      );
    }
  } catch (error) {
    // If it's a permission error, treat as empty success
    if (
      error.message?.includes("403") ||
      error.message?.includes("permission")
    ) {
      yield put(fetchAllPairingsSuccess([]));
    } else {
      yield put(fetchAllPairingsFailure(error.message));
    }
  }
}

function* fetchUserPairings({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("/pairs/my-challenge-pairs"),
      bearer: token,
    });
    if (isSuccessResponse(res)) {
      yield put(fetchUserPairingsSuccess(normalizePairingsResponse(res)));
    } else if (res.status === 403 || res.statusCode === 403) {
      // User has no challenge pairings, treat as empty success
      yield put(fetchUserPairingsSuccess([]));
    } else {
      yield put(
        fetchUserPairingsFailure(res.message || "Unable to load pairings"),
      );
    }
  } catch (error) {
    // If it's a permission error, treat as empty success
    if (
      error.message?.includes("403") ||
      error.message?.includes("permission")
    ) {
      yield put(fetchUserPairingsSuccess([]));
    } else {
      yield put(fetchUserPairingsFailure(error.message));
    }
  }
}

function* fetchCreatorPairings({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("/pairs/me"),
      bearer: token,
    });
    if (isSuccessResponse(res)) {
      yield put(fetchCreatorPairingsSuccess(normalizePairingsResponse(res)));
    } else if (res.status === 403 || res.statusCode === 403) {
      // User is not a creator, treat as empty success
      yield put(fetchCreatorPairingsSuccess([]));
    } else {
      yield put(
        fetchCreatorPairingsFailure(res.message || "Unable to load pairings"),
      );
    }
  } catch (error) {
    // If it's a permission error, treat as empty success
    if (
      error.message?.includes("403") ||
      error.message?.includes("permission")
    ) {
      yield put(fetchCreatorPairingsSuccess([]));
    } else {
      yield put(fetchCreatorPairingsFailure(error.message));
    }
  }
}

function* fetchChallengePairings({ payload: { token, challengeId } }) {
  try {
    const pairsRes = yield queryApi({
      method: "GET",
      url: Urler(`/pairs/challenge/${challengeId}`),
      bearer: token,
    });

    const statusRes = yield queryApi({
      method: "GET",
      url: Urler(`/pairs/my-challenge-pair-status/${challengeId}`),
      bearer: token,
    });

    const pairs = pairsRes.data?.pairs || pairsRes.pairs || [];

    const challenge = pairs[0].challengeId[0];

    if (isSuccessResponse(pairsRes)) {
      yield put(fetchChallengePairingsSuccess({ pairs, challenge }));
    } else {
      yield put(
        fetchChallengePairingsFailure(
          pairsRes.message || "Unable to load pairings",
        ),
      );
    }

    if (isSuccessResponse(statusRes)) {
      yield put(fetchMyPairStatusSuccess(statusRes.data || statusRes));
    }
  } catch (error) {
    yield put(fetchChallengePairingsFailure(error.message));
  }
}

function* fetchMyPairStatus({ payload: { token, challengeId } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler(`/pairs/my-challenge-pair-status/${challengeId}`),
      bearer: token,
    });

    if (res.status === "Success" || res.status === "success") {
      yield put(fetchMyPairStatusSuccess(res.data || res));
    } else {
      yield put(
        fetchMyPairStatusFailure(res.message || "Unable to load pair status"),
      );
    }
  } catch (error) {
    yield put(fetchMyPairStatusFailure(error.message));
  }
}

function* submitLosers({ payload: { token, data } }) {
  try {
    const res = yield queryApi({
      method: "POST",
      url: Urler("/winnings/challenge/loosers"),
      bearer: token,
      data,
    });

    if (isSuccessResponse(res)) {
      yield put(submitLosersSuccess(res.data || res));
    } else {
      yield put(submitLosersFailure(res.message || "Unable to submit losers"));
    }
  } catch (error) {
    yield put(submitLosersFailure(error.message));
  }
}

function* updateLosers({ payload: { token, data } }) {
  try {
    const res = yield queryApi({
      method: "PATCH",
      url: Urler("/winnings/challenge/loosers"),
      bearer: token,
      data,
    });

    if (isSuccessResponse(res)) {
      yield put(updateLosersSuccess(res.data || res));
    } else {
      yield put(updateLosersFailure(res.message || "Unable to update losers"));
    }
  } catch (error) {
    yield put(updateLosersFailure(error.message));
  }
}

function* submitWinners({ payload: { token, data } }) {
  try {
    const res = yield queryApi({
      method: "POST",
      url: Urler("/winnings/challenge"),
      bearer: token,
      data,
    });

    if (isSuccessResponse(res)) {
      yield put(submitWinnersSuccess(res.data || res));
    } else {
      yield put(
        submitWinnersFailure(res.message || "Unable to submit winners"),
      );
    }
  } catch (error) {
    yield put(submitWinnersFailure(error.message));
  }
}

function* updateWinners({ payload: { token, data } }) {
  try {
    const res = yield queryApi({
      method: "PATCH",
      url: Urler("/winnings/challenge"),
      bearer: token,
      data,
    });

    if (isSuccessResponse(res)) {
      yield put(updateWinnersSuccess(res.data || res));
    } else {
      yield put(
        updateWinnersFailure(res.message || "Unable to update winners"),
      );
    }
  } catch (error) {
    yield put(updateWinnersFailure(error.message));
  }
}

function* checkRoundCompletion({ payload: { token, challengeId } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler(`/pairs/challenge/${challengeId}/check-completion`),
      bearer: token,
    });

    if (res.status === "Success" || res.status === "success") {
      yield put(checkRoundCompletionSuccess(res.data || res));
    } else {
      yield put(
        checkRoundCompletionFailure(
          res.message || "Unable to check round completion",
        ),
      );
    }
  } catch (error) {
    yield put(checkRoundCompletionFailure(error.message));
  }
}

function* verifyLosers({
  payload: { token, challengeId, moveToNextRound, iteration, round },
}) {
  try {
    const res = yield queryApi({
      method: "PATCH",
      url: Urler(`/winnings/challenge/moveToNextRound/${challengeId}`),
      bearer: token,
      data: { moveToNextRound, iteration, round },
    });

    if (res.status === "Success" || res.status === "success") {
      yield put(verifyLosersSuccess(res.data || res));
    } else {
      yield put(verifyLosersFailure(res.message || "Unable to verify losers"));
    }
  } catch (error) {
    yield put(verifyLosersFailure(error.message));
  }
}

function* verifyWinners({
  payload: { token, challengeId, verifiedWinners, iteration, round },
}) {
  try {
    console.log("[SAGA] verifyWinners called with:", {
      challengeId,
      verifiedWinners,
      iteration,
      round,
    });
    const res = yield queryApi({
      method: "PATCH",
      url: Urler(`/winnings/challenge/verifyWinning/${challengeId}`),
      bearer: token,
      data: { verifiedWinners, iteration, round },
    });

    console.log("[SAGA] verifyWinners response:", res);
    if (res.status === "Success" || res.status === "success") {
      console.log("[SAGA] verifyWinners success");
      yield put(verifyWinnersSuccess(res.data || res));
    } else {
      console.log("[SAGA] verifyWinners failure:", res.message);
      yield put(
        verifyWinnersFailure(res.message || "Unable to verify winners"),
      );
    }
  } catch (error) {
    console.log("[SAGA] verifyWinners error:", error);
    yield put(verifyWinnersFailure(error.message));
  }
}

export function* fetchPairsWinners({ payload: { token, challengeId } }) {
  try {
    console.log("[SAGA] fetchPairsWinners START", challengeId);
    const pairsRes = yield queryApi({
      method: "GET",
      url: Urler(`/pairs/challenge/${challengeId}`),
      bearer: token,
    });

    console.log("[SAGA] pairsRes:", pairsRes);
    const pairs = pairsRes.data?.pairs || pairsRes.pairs || [];
    console.log("[SAGA] parsed pairs length:", pairs.length);
    const winnersByIteration = {};

    pairs.forEach((pair) => {
      const iter = pair.iterationNumber || 1;
      if (!winnersByIteration[iter]) {
        winnersByIteration[iter] = [];
      }
      winnersByIteration[iter].push(pair);
    });

    const groupedWinners = [];

    Object.entries(winnersByIteration).forEach(([iter, iterPairs]) => {
      console.log(`[SAGA] processing iteration ${iter} with ${iterPairs.length} pairs`);
      const finalRoundNum = Math.max(...iterPairs.map((p) => p.currentRound || 1));
      console.log(`[SAGA] finalRoundNum for iteration ${iter}:`, finalRoundNum);
      const finalPairs = iterPairs.filter(
        (p) => p.currentRound === finalRoundNum && p.winningsMatched === true
      );
      console.log(`[SAGA] finalPairs matched:`, finalPairs.length);

      let firstPlace = [];
      let secondPlace = [];
      let thirdPlace = [];

      finalPairs.forEach((pair) => {
        const allUsers = pair.pairedUsers?.reduce((acc, pu) => {
          if (pu.users) return acc.concat(pu.users);
          return acc;
        }, []) || [];

        if (pair.winners && pair.winners.length > 0) {
          const subWinners = pair.winners[0].winners;

          if (subWinners && Array.isArray(subWinners)) {
            const resolveUser = (userItem) => {
              if (!userItem) return null;
              const userId = typeof userItem === 'object' ? (userItem.id || userItem._id) : userItem;
              const u = allUsers.find(
                (u) => (u._id || u.id)?.toString() === userId?.toString()
              );
              if (u) return u;
              if (typeof userItem === 'object') return { _id: userId, ...userItem };
              return { _id: userId, name: "Unknown" };
            };

            if (subWinners[0])
              firstPlace.push({
                ...resolveUser(subWinners[0]),
                placement: "1st Place",
              });
            if (subWinners[1])
              secondPlace.push({
                ...resolveUser(subWinners[1]),
                placement: "2nd Place",
              });
            if (subWinners[2])
              thirdPlace.push({
                ...resolveUser(subWinners[2]),
                placement: "3rd Place",
              });
          }
        }
      });

      groupedWinners.push({
        iteration: parseInt(iter),
        winners: {
          firstPlace,
          secondPlace,
          thirdPlace,
        },
      });
    });

    groupedWinners.sort((a, b) => a.iteration - b.iteration);
    console.log("[SAGA] fetchPairsWinners SUCCESS:", groupedWinners);
    yield put(fetchPairsWinnersSuccess(groupedWinners));
  } catch (error) {
    console.error("[SAGA] fetchPairsWinners ERROR:", error);
    yield put(fetchPairsWinnersFailure(error.message));
  }
}

export function* pairingsSagas() {
  yield all([
    takeLatest(pairingsActionTypes.FETCH_ALL_PAIRINGS_START, fetchAllPairings),
    takeLatest(
      pairingsActionTypes.FETCH_USER_PAIRINGS_START,
      fetchUserPairings,
    ),
    takeLatest(
      pairingsActionTypes.FETCH_CREATOR_PAIRINGS_START,
      fetchCreatorPairings,
    ),
    takeLatest(
      pairingsActionTypes.FETCH_CHALLENGE_PAIRINGS_START,
      fetchChallengePairings,
    ),
    takeLatest(
      pairingsActionTypes.FETCH_MY_PAIR_STATUS_START,
      fetchMyPairStatus,
    ),
    takeLatest(pairingsActionTypes.SUBMIT_LOSERS_START, submitLosers),
    takeLatest(pairingsActionTypes.UPDATE_LOSERS_START, updateLosers),
    takeLatest(pairingsActionTypes.SUBMIT_WINNERS_START, submitWinners),
    takeLatest(pairingsActionTypes.UPDATE_WINNERS_START, updateWinners),
    takeLatest(
      pairingsActionTypes.CHECK_ROUND_COMPLETION_START,
      checkRoundCompletion,
    ),
    takeLatest(pairingsActionTypes.VERIFY_LOSERS_START, verifyLosers),
    takeLatest(pairingsActionTypes.VERIFY_WINNERS_START, verifyWinners),
    takeLatest(pairingsActionTypes.FETCH_PAIRS_WINNERS_START, fetchPairsWinners),
  ]);
}
